import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { API_URL } from '../../utils/apiConfig';
import { createFollowUpTask, getFollowUpTaskAssignees, getFollowUpTasks, logFollowUpTaskContact, updateFollowUpTask, updateFollowUpTaskStatus } from '../../api/followUpTasks';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { formatFailedActionMessage } from '../../utils/uiMessages';

const FollowUpTasks = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tasks, setTasks] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [assignees, setAssignees] = useState([]);
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [editTaskOpen, setEditTaskOpen] = useState(false);
  const [patientOptions, setPatientOptions] = useState([]);
  const [savingTask, setSavingTask] = useState(false);
  const [savingEditTask, setSavingEditTask] = useState(false);
  const [actionLoadingTaskId, setActionLoadingTaskId] = useState('');
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuTask, setActionMenuTask] = useState(null);
  const [contactDialog, setContactDialog] = useState({
    open: false,
    task: null,
    channel: 'Call',
    title: '',
    message: '',
  });
  const [newTask, setNewTask] = useState({
    patientId: '',
    title: '',
    description: '',
    type: 'followup',
    priority: 'medium',
    dueAt: '',
    assignedTo: '',
  });
  const [editTask, setEditTask] = useState({
    id: '',
    title: '',
    description: '',
    type: 'followup',
    priority: 'medium',
    dueAt: '',
    assignedTo: '',
    status: 'open',
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    mine: 'true',
    patientId: '',
    overdue: '',
    source: '',
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const patientId = params.get('patientId') || '';
    if (patientId) {
      setFilters((prev) => ({ ...prev, patientId }));
    }
  }, [location.search]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const response = await getFollowUpTasks({
        search: filters.search || undefined,
        status: filters.status || undefined,
        mine: filters.mine || undefined,
        patientId: filters.patientId || undefined,
        overdue: filters.overdue || undefined,
        source: filters.source || undefined,
      });
      setTasks(response.data || []);
      setOverdueCount(response.overdueCount || 0);
      setError('');
    } catch (err) {
      setError(getApiErrorMessage(err, formatFailedActionMessage('load', 'follow-up tasks')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.mine, filters.overdue, filters.source]);

  useEffect(() => {
    const handler = () => loadTasks();
    window.addEventListener('clinic-scope-changed', handler);
    return () => window.removeEventListener('clinic-scope-changed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      loadTasks();
    }, 400);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  useEffect(() => {
    const loadAssignees = async () => {
      try {
        const response = await getFollowUpTaskAssignees();
        setAssignees(response?.data || []);
      } catch (err) {
        setAssignees([]);
      }
    };
    loadAssignees();
  }, []);

  const handleStatusChange = async (taskId, status) => {
    try {
      await updateFollowUpTaskStatus(taskId, status);
      toast.success('Task status updated');
      await loadTasks();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('update', 'task')));
    }
  };

  const searchPatients = async (term = '') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/patients`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: 1,
          limit: 30,
          search: term || undefined,
        },
      });
      setPatientOptions(response?.data?.data || []);
    } catch (err) {
      setPatientOptions([]);
    }
  };

  const openNewTaskDialog = async () => {
    setNewTaskOpen(true);
    await searchPatients('');
    setNewTask((prev) => ({
      ...prev,
      assignedTo: prev.assignedTo || assignees[0]?._id || '',
      patientId: filters.patientId || prev.patientId,
    }));
  };

  const handleCreateTask = async () => {
    if (!newTask.patientId || !newTask.title.trim()) {
      toast.error('Patient and title are required');
      return;
    }
    try {
      setSavingTask(true);
      await createFollowUpTask({
        patientId: newTask.patientId,
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        type: newTask.type,
        priority: newTask.priority,
        dueAt: newTask.dueAt || undefined,
        assignedTo: newTask.assignedTo || undefined,
      });
      toast.success('Task created');
      setNewTaskOpen(false);
      setNewTask({
        patientId: '',
        title: '',
        description: '',
        type: 'followup',
        priority: 'medium',
        dueAt: '',
        assignedTo: '',
      });
      await loadTasks();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('create', 'task')));
    } finally {
      setSavingTask(false);
    }
  };

  const openEditTaskDialog = (task) => {
    setEditTask({
      id: task._id,
      title: task.title || '',
      description: task.description || '',
      type: task.type || 'followup',
      priority: task.priority || 'medium',
      dueAt: task.dueAt ? new Date(task.dueAt).toISOString().slice(0, 16) : '',
      assignedTo: task.assignedTo?._id || '',
      status: task.status || 'open',
    });
    setEditTaskOpen(true);
  };

  const handleUpdateTask = async () => {
    if (!editTask.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      setSavingEditTask(true);
      await updateFollowUpTask(editTask.id, {
        title: editTask.title.trim(),
        description: editTask.description.trim(),
        type: editTask.type,
        priority: editTask.priority,
        dueAt: editTask.dueAt || null,
        assignedTo: editTask.assignedTo || null,
        status: editTask.status,
      });
      toast.success('Task updated');
      setEditTaskOpen(false);
      await loadTasks();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('update', 'task')));
    } finally {
      setSavingEditTask(false);
    }
  };

  const buildTemplateMessage = (channel, task) => {
    const patientName = task?.patient?.name || 'patient';
    const templates = {
      Call: `Call note: Spoke with ${patientName} regarding "${task.title}".`,
      WhatsApp: `Hello ${patientName}, this is a follow-up from our clinic regarding "${task.title}". Please let us know a convenient time.`,
      Email: `Hello ${patientName},\n\nThis is a follow-up from our clinic regarding "${task.title}". Please reply with your preferred time.\n\nBest regards,\nDentalOS Team`,
    };
    return templates[channel] || templates.Call;
  };

  const extractDigits = (value = '') => String(value).replace(/\D/g, '');

  const openContactDialog = (task, channel) => {
    setContactDialog({
      open: true,
      task,
      channel,
      title: channel === 'Call' ? 'Log call note' : `Send ${channel}`,
      message: buildTemplateMessage(channel, task),
    });
  };

  const submitContactDialog = async () => {
    const { task, channel, message } = contactDialog;
    const finalMessage = String(message || '').trim();
    if (!task || !finalMessage) {
      toast.error('Message cannot be empty');
      return;
    }

    try {
      setActionLoadingTaskId(task._id);
      const phone = extractDigits(task?.patient?.phone || '');
      const email = String(task?.patient?.email || '').trim();
      if ((channel === 'Call' || channel === 'WhatsApp') && !phone) {
        toast.error('Patient phone is missing');
        return;
      }
      if (channel === 'Email' && !email) {
        toast.error('Patient email is missing');
        return;
      }

      if (channel === 'Call') {
        window.open(`tel:${phone}`, '_self');
      } else if (channel === 'WhatsApp') {
        const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(finalMessage)}`;
        window.open(waUrl, '_blank', 'noopener,noreferrer');
      } else if (channel === 'Email') {
        const subject = encodeURIComponent('Follow-up regarding your treatment');
        const body = encodeURIComponent(finalMessage);
        window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
      }

      await logFollowUpTaskContact(task._id, {
        channel,
        message: finalMessage,
        template: channel === 'Call' ? 'manual_contact_note' : `${channel.toLowerCase()}_followup`,
      });

      toast.success(`${channel} logged in communications`);
      setContactDialog({ open: false, task: null, channel: 'Call', title: '', message: '' });
      await loadTasks();
    } catch (err) {
      toast.error(getApiErrorMessage(err, formatFailedActionMessage('log', 'communication')));
    } finally {
      setActionLoadingTaskId('');
    }
  };

  const handleOpenActionMenu = (event, task) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuTask(task);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuTask(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4">Follow-up Tasks</Typography>
          {overdueCount > 0 && (
            <Chip size="small" color="error" label={`Overdue: ${overdueCount}`} sx={{ mt: 0.5 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={openNewTaskDialog}>
            New Task
          </Button>
          <Button variant="outlined" onClick={() => navigate('/patients/pipeline')}>
            Open Pipeline
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Search patient"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="done">Done</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Assignee</InputLabel>
            <Select
              value={filters.mine}
              label="Assignee"
              onChange={(e) => setFilters((prev) => ({ ...prev, mine: e.target.value }))}
            >
              <MenuItem value="true">My tasks</MenuItem>
              <MenuItem value="">All tasks</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>SLA</InputLabel>
            <Select
              value={filters.overdue}
              label="SLA"
              onChange={(e) => setFilters((prev) => ({ ...prev, overdue: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Overdue only</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Source</InputLabel>
            <Select
              value={filters.source}
              label="Source"
              onChange={(e) => setFilters((prev) => ({ ...prev, source: e.target.value }))}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="manual">Manual</MenuItem>
              <MenuItem value="pipeline_reminder">Pipeline reminder</MenuItem>
              <MenuItem value="no_show_recovery">No-show recovery</MenuItem>
              <MenuItem value="recall_engine">Recall engine</MenuItem>
              <MenuItem value="reactivation_factory">Reactivation factory</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={loadTasks}>Search</Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Task</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Assignee</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Due</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task) => {
                  const isOverdue =
                    (['open', 'in_progress'].includes(task.status) && task.dueAt && new Date(task.dueAt).getTime() <= Date.now()) ||
                    (['open', 'in_progress'].includes(task.status) && !task.dueAt && (Date.now() - new Date(task.createdAt).getTime()) >= 24 * 60 * 60 * 1000);
                  return (
                  <TableRow key={task._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{task.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{task.description || '—'}</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip size="small" variant="outlined" label={task.source || 'manual'} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => navigate(`/patients/${task.patient?._id}`)}>
                        {task.patient?.name || task.patient?.patientId || 'Patient'}
                      </Button>
                    </TableCell>
                    <TableCell>{task.assignedTo?.name || task.assignedTo?.email || '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip size="small" label={task.priority} />
                        {isOverdue && <Chip size="small" color="error" label="Overdue" />}
                      </Box>
                    </TableCell>
                    <TableCell>{task.dueAt ? new Date(task.dueAt).toLocaleString() : '—'}</TableCell>
                    <TableCell><Chip size="small" label={task.status} color={task.status === 'done' ? 'success' : task.status === 'in_progress' ? 'primary' : 'default'} /></TableCell>
                    <TableCell align="right">
                      {isMobile ? (
                        <IconButton
                          size="small"
                          onClick={(event) => handleOpenActionMenu(event, task)}
                          disabled={actionLoadingTaskId === task._id}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <>
                          <Button size="small" disabled={actionLoadingTaskId === task._id} onClick={() => openEditTaskDialog(task)}>Edit</Button>
                          <Button size="small" disabled={actionLoadingTaskId === task._id} onClick={() => openContactDialog(task, 'Call')}>Call</Button>
                          <Button size="small" disabled={actionLoadingTaskId === task._id} onClick={() => openContactDialog(task, 'WhatsApp')}>WhatsApp</Button>
                          <Button size="small" disabled={actionLoadingTaskId === task._id} onClick={() => openContactDialog(task, 'Email')}>Email</Button>
                          {task.status !== 'in_progress' && task.status !== 'done' && (
                            <Button size="small" disabled={actionLoadingTaskId === task._id} onClick={() => handleStatusChange(task._id, 'in_progress')}>Start</Button>
                          )}
                          {task.status !== 'done' && (
                            <Button size="small" color="success" disabled={actionLoadingTaskId === task._id} onClick={() => handleStatusChange(task._id, 'done')}>Done</Button>
                          )}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )})}
                {tasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No follow-up tasks</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleCloseActionMenu}
      >
        <MenuItem onClick={() => { if (actionMenuTask) openEditTaskDialog(actionMenuTask); handleCloseActionMenu(); }}>Edit</MenuItem>
        <MenuItem onClick={() => { if (actionMenuTask) openContactDialog(actionMenuTask, 'Call'); handleCloseActionMenu(); }}>Call</MenuItem>
        <MenuItem onClick={() => { if (actionMenuTask) openContactDialog(actionMenuTask, 'WhatsApp'); handleCloseActionMenu(); }}>WhatsApp</MenuItem>
        <MenuItem onClick={() => { if (actionMenuTask) openContactDialog(actionMenuTask, 'Email'); handleCloseActionMenu(); }}>Email</MenuItem>
        {actionMenuTask?.status !== 'in_progress' && actionMenuTask?.status !== 'done' && (
          <MenuItem onClick={() => { handleStatusChange(actionMenuTask._id, 'in_progress'); handleCloseActionMenu(); }}>Start</MenuItem>
        )}
        {actionMenuTask?.status !== 'done' && (
          <MenuItem onClick={() => { handleStatusChange(actionMenuTask._id, 'done'); handleCloseActionMenu(); }}>Done</MenuItem>
        )}
      </Menu>

      <Dialog open={contactDialog.open} onClose={() => setContactDialog({ open: false, task: null, channel: 'Call', title: '', message: '' })} fullWidth maxWidth="sm">
        <DialogTitle>{contactDialog.title}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
            label="Message / note"
            value={contactDialog.message}
            onChange={(e) => setContactDialog((prev) => ({ ...prev, message: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialog({ open: false, task: null, channel: 'Call', title: '', message: '' })}>Cancel</Button>
          <Button variant="contained" onClick={submitContactDialog} disabled={Boolean(actionLoadingTaskId)}>
            {actionLoadingTaskId ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newTaskOpen} onClose={() => setNewTaskOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create Follow-up Task</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Search patient"
            sx={{ mt: 1, mb: 2 }}
            onChange={(e) => searchPatients(e.target.value)}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Patient</InputLabel>
            <Select
              label="Patient"
              value={newTask.patientId}
              onChange={(e) => setNewTask((prev) => ({ ...prev, patientId: e.target.value }))}
            >
              {patientOptions.map((p) => (
                <MenuItem key={p._id} value={p._id}>
                  {p.name || 'Unnamed'} ({p.patientId || p._id}) {p.phone ? `- ${p.phone}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="Title"
            sx={{ mb: 2 }}
            value={newTask.title}
            onChange={(e) => setNewTask((prev) => ({ ...prev, title: e.target.value }))}
          />
          <TextField
            fullWidth
            size="small"
            label="Description"
            multiline
            minRows={2}
            sx={{ mb: 2 }}
            value={newTask.description}
            onChange={(e) => setNewTask((prev) => ({ ...prev, description: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={newTask.type}
                onChange={(e) => setNewTask((prev) => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="call">Call</MenuItem>
                <MenuItem value="message">Message</MenuItem>
                <MenuItem value="invite">Invite</MenuItem>
                <MenuItem value="followup">Follow-up</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={newTask.priority}
                onChange={(e) => setNewTask((prev) => ({ ...prev, priority: e.target.value }))}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Due at"
              InputLabelProps={{ shrink: true }}
              value={newTask.dueAt}
              onChange={(e) => setNewTask((prev) => ({ ...prev, dueAt: e.target.value }))}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Assign to</InputLabel>
              <Select
                label="Assign to"
                value={newTask.assignedTo}
                onChange={(e) => setNewTask((prev) => ({ ...prev, assignedTo: e.target.value }))}
              >
                <MenuItem value="">Me</MenuItem>
                {assignees.map((u) => (
                  <MenuItem key={u._id} value={u._id}>
                    {u.name || u.email} ({u.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTaskOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateTask} variant="contained" disabled={savingTask}>
            {savingTask ? 'Saving...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editTaskOpen} onClose={() => setEditTaskOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Follow-up Task</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Title"
            sx={{ mt: 1, mb: 2 }}
            value={editTask.title}
            onChange={(e) => setEditTask((prev) => ({ ...prev, title: e.target.value }))}
          />
          <TextField
            fullWidth
            size="small"
            label="Description"
            multiline
            minRows={2}
            sx={{ mb: 2 }}
            value={editTask.description}
            onChange={(e) => setEditTask((prev) => ({ ...prev, description: e.target.value }))}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                label="Type"
                value={editTask.type}
                onChange={(e) => setEditTask((prev) => ({ ...prev, type: e.target.value }))}
              >
                <MenuItem value="call">Call</MenuItem>
                <MenuItem value="message">Message</MenuItem>
                <MenuItem value="invite">Invite</MenuItem>
                <MenuItem value="followup">Follow-up</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                label="Priority"
                value={editTask.priority}
                onChange={(e) => setEditTask((prev) => ({ ...prev, priority: e.target.value }))}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="Due at"
              InputLabelProps={{ shrink: true }}
              value={editTask.dueAt}
              onChange={(e) => setEditTask((prev) => ({ ...prev, dueAt: e.target.value }))}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Assign to</InputLabel>
              <Select
                label="Assign to"
                value={editTask.assignedTo}
                onChange={(e) => setEditTask((prev) => ({ ...prev, assignedTo: e.target.value }))}
              >
                <MenuItem value="">Me</MenuItem>
                {assignees.map((u) => (
                  <MenuItem key={u._id} value={u._id}>
                    {u.name || u.email} ({u.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={editTask.status}
                onChange={(e) => setEditTask((prev) => ({ ...prev, status: e.target.value }))}
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="done">Done</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTaskOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateTask} variant="contained" disabled={savingEditTask}>
            {savingEditTask ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FollowUpTasks;
